import {
  convertAppointmentResponseToModel,
  convertToAppointmentInput
} from "utils/appointments.util";
import { formatValidQueryParamUrlCommaSeparated } from "utils/common";
import { createPaginatedEndpoint } from "utils/services.util";

import { emptySplitApi } from "./empty-split-api";
import { EMPTY_STAFF_ID } from "./events";
import {
  Appointment,
  AppointmentCounters,
  AppointmentCountersResponse,
  AppointmentRecurrenceInput,
  AppointmentResponse,
  AppointmentSkeleton,
  AppointmentsDownloadQuery,
  AppointmentsResponse,
  DeleteAppointmentInput,
  NewAppointmentsResponse,
  PatientAppointmentsFilters,
  QuerySearchFilter,
  RoomValidationRequest,
  RoomValidationResponse
} from "./types/appointments.type";
import { GoogleCalendarSyncInput } from "./types/calendar-integration.type";
import { Diagnosis } from "./types/diagnosis.type";
import { CSVReportResponse } from "./types/reports.type";

const APPOINTMENT_DEFAULT_QUERY =
  "{id,podium_id,podium_sync,title,assigned_tags,rescheduled_appointment_id,needs_reschedule,patient_feedback_rating,outcome,outcome_reason,outcome_notes,start_moment,end_moment,status,companion_name,companion_type,notes,physician_referral,referral_source{id,name},sub_referral_source{id,name},referring_physician,staff_member{id,first_name,last_name,suffix,title,is_active},is_direct_supervisor,supervising_provider{id,first_name,last_name,suffix,title,is_active},room{id,name,clinic{id,name,practice{id,name,logo}}},clinic{id,timezone,name,practice{id,name,logo}},type{id,name,default_duration,color,is_opportunity,general_type,generate_sale},chart_note,crud_events,should_create_schedule_for_staff,should_create_intake_form_request,patient{id,first_name,last_name,title,suffix,preferred_name,middle_name,photo,phone,phone_type,email,birthdate,preferred_communication_method,do_not_email,do_not_message,do_not_call,do_not_mail},diagnosis,hearing_test_conducted,companion_present,hearing_aid_demo_performed,telehealth_provider,zoom_join_url,zoom_meeting_id,should_notify,notification_method,timer{id,status,status_display,minutes_accumulated,last_started_at,completed}}";

export const appointmentsApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    queryAppointmentsCounter: builder.query<
      AppointmentCounters,
      Omit<QuerySearchFilter, "query" | "limit" | "offset">
    >({
      query: ({ staff_member, clinic, ...filters }) =>
        `/appointments/counters/?${formatValidQueryParamUrlCommaSeparated({
          ...filters,
          clinic: clinic?.join(",") || undefined,
          staff_member: staff_member?.join(",") || EMPTY_STAFF_ID.toString(),
          status: undefined
        })}`,
      transformResponse: (response: AppointmentCountersResponse) => ({
        appointmentAll: response.appointment_all ?? 0,
        appointmentAllNoDrafts: response.appointment_all_no_drafts ?? 0,
        appointmentDraft: response.appointment_draft ?? 0,
        appointmentUnconfirmed: response.appointment_unconfirmed ?? 0,
        appointmentOpportunity: response.appointment_opportunity ?? 0,
        appointmentIncomplete: response.appointment_incomplete ?? 0,
        appointmentCancelledRescheduled:
          response.appointment_cancelled_rescheduled ?? 0
      }),
      providesTags: ["AppointmentCounters", "Appointments", "Events"]
    }),
    queryAppointmentsWithEmailsAndPhoneCount: builder.query<
      { count: number; count_sms: number; count_distinct_patients: number },
      any
    >({
      query: (filters) =>
        `/appointments/patients-count/${formatValidQueryParamUrlCommaSeparated(
          filters,
          true
        )}`,
      providesTags: ["Appointments"],
      transformResponse: (response: {
        count: number;
        count_sms: number;
        count_distinct_patients: number;
      }) => ({
        count: Number(response.count),
        count_sms: Number(response.count_sms),
        count_distinct_patients: Number(response.count_distinct_patients)
      })
    }),
    getAppointments: builder.query<
      AppointmentsResponse,
      Partial<PatientAppointmentsFilters>
    >({
      query: ({
        offset = 0,
        limit = 25,
        patientId,
        baseFilter,
        ordering = "-start_moment",
        query = "{id,notes,needs_reschedule,start_moment,status,referral_source{id,name},staff_member{id,first_name,last_name,suffix,title,is_active},clinic{id,timezone,name,logo,practice{id,name,logo}},patient{id,first_name,last_name},type{id,name,default_duration,color},extra,physician_referral,outcome,outcome_reason,outcome_notes,patient_feedback_rating,should_create_schedule_for_staff,should_create_intake_form_request}",
        ...filters
      }) =>
        `/appointments/?limit=${limit}&offset=${offset}&ordering=${ordering}&${formatValidQueryParamUrlCommaSeparated(
          {
            ...baseFilter,
            ...filters,
            patient: patientId,
            query
          }
        )}`,
      providesTags: ["Appointments"],
      transformResponse: (
        response: AppointmentResponse[] | NewAppointmentsResponse
      ) => {
        if ("count" in response) {
          return response.results.map(convertAppointmentResponseToModel);
        }

        return response.map(convertAppointmentResponseToModel);
      }
    }),
    getReportAppointments: builder.query<
      AppointmentsResponse,
      Partial<PatientAppointmentsFilters>
    >({
      query: ({
        offset = 0,
        limit = 25,
        upToDate,
        fromDate,
        patientId,
        baseFilter,
        ordering = "-start_moment",
        query = "{id,notes,rescheduled_appointment_id,needs_reschedule,start_moment,status,referral_source{id,name},staff_member{id,first_name,last_name,suffix,title,is_active},clinic{id,timezone,name,logo,practice{id,name,logo}},patient{id,first_name,last_name},type{id,name,default_duration},extra,physician_referral,outcome,outcome_reason,outcome_notes,patient_feedback_rating,should_create_schedule_for_staff,should_create_intake_form_request}",
        ...filters
      }) =>
        `/appointments-report/?limit=${limit}&offset=${offset}&ordering=${ordering}&${formatValidQueryParamUrlCommaSeparated(
          {
            ...baseFilter,
            ...filters,
            patient: patientId,
            query
          }
        )}`,
      providesTags: ["Appointments"],
      transformResponse: (
        response: AppointmentResponse[] | NewAppointmentsResponse
      ) => {
        if ("count" in response) {
          return response.results.map(convertAppointmentResponseToModel);
        }

        return response.map(convertAppointmentResponseToModel);
      }
    }),
    getPatientAppointmentsPaginated: createPaginatedEndpoint(builder, {
      queryFn: ({
        offset = 0,
        limit = 25,
        upToDate,
        patientId,
        fromDate,
        baseFilter,
        ordering = "-start_moment",
        query = "{id,schedule,notes,replacement_appointment{id},needs_reschedule,patient_feedback_rating,outcome,outcome_reason,title,outcome_notes,start_moment,end_moment,status,referral_source{id,name},staff_member{id,first_name,last_name,suffix,title,is_active},room{clinic{id,name,logo,practice{id,name,logo}}},clinic{id,timezone,name,logo,practice{id,name,logo}},type{id,name,default_duration,is_opportunity,general_type,color},assigned_tags,patient{id,first_name,last_name},should_notify,notification_method}",
        ...filters
      }) =>
        `/appointments/?limit=${limit}&offset=${offset}&ordering=${ordering}&after_moment=${
          fromDate ?? ""
        }&before_moment=${
          upToDate ?? ""
        }&${formatValidQueryParamUrlCommaSeparated({
          ...baseFilter,
          ...filters,
          patient: patientId,
          query
        })}`,
      providesTags: ["Appointments"],
      transformResponseFn: (
        response: AppointmentResponse[] | NewAppointmentsResponse
      ) => {
        if ("count" in response) {
          return response.results.map(convertAppointmentResponseToModel);
        }

        return response.map(convertAppointmentResponseToModel);
      }
    }),
    getPatientAppointments: builder.query<
      AppointmentsResponse,
      PatientAppointmentsFilters
    >({
      query: ({
        offset = 0,
        limit = 25,
        upToDate,
        patientId,
        fromDate,
        baseFilter,
        ordering = "-start_moment",
        query = "{id,notes,replacement_appointment{id},patient_feedback_rating,outcome,outcome_reason,title,outcome_notes,start_moment,end_moment,status,referral_source{id,name},staff_member{id,first_name,last_name,suffix,title,is_active},room{clinic{id,name,logo,practice{id,name,logo}}},clinic{id,timezone,name,logo,practice{id,name,logo}},type{id,name,default_duration,is_opportunity,general_type},assigned_tags}",
        ...filters
      }) =>
        `/appointments/?limit=${limit}&offset=${offset}&ordering=${ordering}&after_moment=${
          fromDate ?? ""
        }&before_moment=${
          upToDate ?? ""
        }&${formatValidQueryParamUrlCommaSeparated({
          ...baseFilter,
          ...filters,
          patient: patientId,
          query
        })}`,

      providesTags: (result = []) =>
        result?.map((appointment) => ({
          type: "Appointments" as const,
          id: appointment.id
        })),

      transformResponse: (
        response: AppointmentResponse[] | NewAppointmentsResponse
      ) => {
        if ("count" in response) {
          return response.results.map(convertAppointmentResponseToModel);
        }

        return response.map(convertAppointmentResponseToModel);
      }
    }),
    getAppointment: builder.query<Appointment, { id: number; query?: string }>({
      query: ({ id, query = APPOINTMENT_DEFAULT_QUERY }) =>
        `/appointments/${id}/?query=${query}`,
      providesTags: (result) => [
        {
          type: "Appointments" as const,
          id: result?.id
        }
      ],
      transformResponse: convertAppointmentResponseToModel
    }),
    addAppointment: builder.mutation<AppointmentSkeleton, Partial<Appointment>>(
      {
        query: (appointmentData) => ({
          url: "/appointments/",
          method: "POST",
          body: convertToAppointmentInput(appointmentData)
        }),
        invalidatesTags: (result, _error, arg) => [
          "Appointments",
          "Events",
          "Patients",
          "StaffAvailability",
          "SubReferralSources",
          "RoomTraffic",
          "OnboardingFormRequest",
          ...(result?.patient
            ? [{ type: "Patients" as const, id: result.patient }]
            : arg.patient?.id
              ? [{ type: "Patients" as const, id: arg.patient.id }]
              : [])
        ]
      }
    ),
    updateAppointment: builder.mutation<
      AppointmentSkeleton,
      Partial<Omit<Appointment, "diagnosis">> &
        Partial<{
          diagnosis: Partial<Diagnosis>;
        }> &
        AppointmentRecurrenceInput
    >({
      query: ({ id, ...appointmentData }) => ({
        url: `/appointments/${id}/`,
        method: "PATCH",
        body: convertToAppointmentInput(appointmentData)
      }),
      invalidatesTags: (result, _error, arg) => [
        "Appointments",
        "Events",
        "Patients",
        "ChartNotes",
        "StaffAvailability",
        "SubReferralSources",
        "RoomTraffic",
        "AssignedAppointmentTags",
        "OnboardingFormRequest",
        ...(result?.patient
          ? [
              { type: "Patients" as const, id: result.patient },
              { type: "ActiveVisitChecklists" as const, id: `LIST-${result.patient}` }
            ]
          : arg.patient?.id
            ? [
                { type: "Patients" as const, id: arg.patient.id },
                { type: "ActiveVisitChecklists" as const, id: `LIST-${arg.patient.id}` }
              ]
            : [])
      ]
    }),
    syncAppointmentsToGoogle: builder.mutation<
      {
        status: string;
        params: GoogleCalendarSyncInput;
      },
      GoogleCalendarSyncInput
    >({
      query: (params) => ({
        url: "/appointments/google-calendar/sync/",
        method: "POST",
        body: params
      })
    }),
    syncAppointmentToGoogle: builder.mutation<string, number>({
      query: (id) => ({
        url: `/appointments/${id}/sync/`,
        method: "PATCH"
      })
    }),
    zoomSyncAppointment: builder.mutation<{ detail: string }, number>({
      query: (id) => ({
        url: `/appointments/${id}/zoom-sync/`,
        method: "POST"
      })
    }),
    deleteAppointment: builder.mutation<string, DeleteAppointmentInput>({
      query: ({ appointmentId, start_recurrence_id }) => ({
        url: `/appointments/${appointmentId}/`,
        method: "DELETE",
        body: { start_recurrence_id }
      }),
      invalidatesTags: [
        "Appointments",
        "Events",
        "StaffAvailability",
        "RoomTraffic"
      ]
    }),
    bulkDeleteAppointments: builder.mutation<string, { ids: number[] }>({
      query: ({ ids }) => ({
        url: "/appointments/bulk-delete/",
        method: "DELETE",
        body: { ids }
      }),
      invalidatesTags: [
        "Appointments",
        "Events",
        "StaffAvailability",
        "RoomTraffic"
      ]
    }),
    reassignProviderAppointments: builder.mutation<
      { reassigned_count: number },
      {
        from_staff_member: number;
        to_staff_member: number;
        from_date: string;
      }
    >({
      query: (body) => ({
        url: "/appointments/bulk-reassign-provider/",
        method: "POST",
        body
      }),
      invalidatesTags: [
        "Appointments",
        "Events",
        "StaffAvailability",
        "RoomTraffic"
      ]
    }),
    validateRoomAvailability: builder.mutation<
      RoomValidationResponse,
      RoomValidationRequest
    >({
      query: (body) => ({
        url: "/appointments/",
        method: "POST",
        body
      })
    }),
    downloadAppointmentsCSV: builder.query<
      CSVReportResponse,
      AppointmentsDownloadQuery
    >({
      query: ({ query = "{*}", csvHeader = "*", csvLabels, ...filters }) => ({
        url: `/appointments/?ordering=-start_moment&query=${query}&csv_header=${csvHeader}&${formatValidQueryParamUrlCommaSeparated(
          {
            csv_labels: csvLabels,
            ...filters
          }
        )}`,
        headers: { Accept: "custom-csv/json" }
      })
    })
  })
});

export const {
  useLazyQueryAppointmentsCounterQuery,
  useAddAppointmentMutation,
  useDeleteAppointmentMutation,
  useUpdateAppointmentMutation,
  useGetPatientAppointmentsQuery,
  useGetAppointmentsQuery,
  useLazyGetAppointmentsQuery,
  useGetReportAppointmentsQuery,
  useGetAppointmentQuery,
  useLazyGetAppointmentQuery,
  useLazyDownloadAppointmentsCSVQuery,
  useBulkDeleteAppointmentsMutation,
  useSyncAppointmentsToGoogleMutation,
  useSyncAppointmentToGoogleMutation,
  useQueryAppointmentsWithEmailsAndPhoneCountQuery,
  useValidateRoomAvailabilityMutation,
  useZoomSyncAppointmentMutation,
  useReassignProviderAppointmentsMutation
} = appointmentsApi;
